import React, { useMemo, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  KeyboardArrowDown as ArrowIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const CustomSelect = ({
  options = [],
  value = "",
  onChange,
  label = "",
  placeholder = "Select...",
  error = false,
  helperText = "",
  required = false,
  fullWidth = true,
  size = "small",
  disabled = false,
  name = "",
  minWidth = "150px",
  searchable = false,
  searchPlaceholder = "Search...",
  noOptionsText = "No options found",
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const handleChange = (event) => {
    const newValue = event.target.value;
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: newValue,
        },
      });
    }
    setSearchTerm("");
  };

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter((opt) =>
      String(opt.label ?? opt.value ?? "").toLowerCase().includes(term)
    );
  }, [options, searchTerm, searchable]);

  const handleSearchChange = (e) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
  };

  const handleSearchClear = (e) => {
    e.stopPropagation();
    setSearchTerm("");
  };

  const handleSearchKeyDown = (e) => {
    e.stopPropagation();
  };

  return (
    <Box
      sx={{
        width: fullWidth ? "100%" : "auto",
        minWidth: fullWidth ? "auto" : minWidth,
      }}
    >
      <TextField
        select
        fullWidth
        size={size}
        label={label}
        value={value ?? ""}
        onChange={handleChange}
        disabled={disabled}
        error={error}
        helperText={helperText}
        required={required}
        name={name}
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          displayEmpty: true,
          notched: true,
          IconComponent: ArrowIcon,
          MenuProps: {
            autoFocus: searchable,
            PaperProps: {
              sx: {
                mt: 0.5,
                borderRadius: "10px",
                boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                maxHeight: 320,
              },
            },
          },
          onClose: () => setSearchTerm(""),
          renderValue: (selected) => {
            if (!selected || selected === "") {
              return (
                <Typography sx={{ color: "#94A3B8", fontSize: "0.875rem" }}>
                  {placeholder}
                </Typography>
              );
            }
            const option = options?.find(
              (opt) => String(opt.value) === String(selected)
            );
            return (
              <Typography sx={{ color: "#0F172A", fontSize: "0.875rem" }}>
                {option?.label || selected}
              </Typography>
            );
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            backgroundColor: disabled ? "#F1F5F9" : "#FFFFFF",
            minWidth: fullWidth ? "auto" : minWidth,
            "&:hover fieldset": {
              borderColor: error ? "#EF4444" : "#3B82F6",
            },
            "& fieldset": {
              borderColor: error ? "#EF4444" : "#E2E8F0",
            },
            "&.Mui-focused fieldset": {
              borderColor: error ? "#EF4444" : "#3B82F6",
              borderWidth: 2,
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.75rem",
            fontWeight: 500,
            color: "#475467",
            backgroundColor: "#FFFFFF",
            px: 0.5,
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: "#3B82F6",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            minHeight: "20px",
            padding: "10px 14px",
            minWidth: fullWidth ? "auto" : minWidth,
          },
        }}
      >
        {searchable && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              bgcolor: "#FFFFFF",
              px: 1,
              pt: 1,
              pb: 0.5,
            }}
            onKeyDown={handleSearchKeyDown}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleSearchClear}
                      edge="end"
                      sx={{ p: 0.3 }}
                    >
                      <CloseIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                sx: {
                  borderRadius: "8px",
                  fontSize: "0.85rem",
                  bgcolor: "#F8FAFC",
                },
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        )}

        {filteredOptions.length === 0 ? (
          <Box sx={{ px: 2, py: 1.5, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.82rem", color: "#94A3B8" }}>
              {noOptionsText}
            </Typography>
          </Box>
        ) : (
          filteredOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                fontSize: "0.875rem",
                color: "#0F172A",
                py: 1.5,
                "&.Mui-selected": {
                  backgroundColor: "rgba(59, 130, 246, 0.06)",
                  color: "#3B82F6",
                  fontWeight: 500,
                },
                "&:hover": {
                  backgroundColor: "rgba(59, 130, 246, 0.04)",
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))
        )}
      </TextField>
    </Box>
  );
};

export default CustomSelect;